import {
  observeAudioForE2e,
  recordAudioPlayAttemptForE2e,
  recordAudioPlayRejectionForE2e,
  recordAudioPlayResolutionForE2e,
} from "./audioE2eProbe.client";

let primedElement: HTMLAudioElement | null = null;
let primedSourceUrl: string | null = null;
let activePlaybackElement: HTMLAudioElement | null = null;
let releaseActivePlayback: (() => void) | null = null;
const forceMutedForE2e = import.meta.env.VITE_AUDIO_E2E_MUTED === "1";

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function createSilentWavUrl(): string {
  const sampleRate = 8_000;
  const sampleCount = 2_000;
  const buffer = new ArrayBuffer(44 + sampleCount);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + sampleCount, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true);
  view.setUint16(32, 1, true);
  view.setUint16(34, 8, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, sampleCount, true);
  new Uint8Array(buffer, 44).fill(128);

  return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}

function ensurePrimedElement(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (primedElement) return primedElement;

  primedSourceUrl = createSilentWavUrl();
  primedElement = new Audio();
  observeAudioForE2e(primedElement);
  primedElement.loop = true;
  primedElement.muted = forceMutedForE2e;
  primedElement.preload = "auto";
  primedElement.src = primedSourceUrl;
  primedElement.load();
  return primedElement;
}

export function prepareAutoplayAudioElement(): void {
  ensurePrimedElement();
}

export async function primeAutoplayAudioElementFromGesture(): Promise<void> {
  // Route and store state can already have unmounted while a detached native
  // element still has a pending play promise. Destroy its sequence rather
  // than only pausing: iOS can resolve an in-flight play after pause and start
  // the old element again unless its source and callbacks are invalidated.
  const release = releaseActivePlayback;
  releaseActivePlayback = null;
  activePlaybackElement = null;
  release?.();

  const element = ensurePrimedElement();
  if (!element?.paused) return;

  // The invocation itself stays in the trusted gesture stack. Navigation waits
  // for Safari to start this element. It must remain playing until the real
  // source is installed: pausing here discards iOS's transient media grant.
  recordAudioPlayAttemptForE2e(element);
  try {
    await element.play();
    recordAudioPlayResolutionForE2e(element);
  } catch (error) {
    recordAudioPlayRejectionForE2e(element, error);
    // Navigation must still proceed and the normal player UI remains usable.
  }
}

export function registerAutoplayPlaybackElement(element: HTMLAudioElement, release: () => void): void {
  activePlaybackElement = element;
  releaseActivePlayback = release;
}

export function unregisterAutoplayPlaybackElement(element: HTMLAudioElement): void {
  if (activePlaybackElement === element) {
    activePlaybackElement = null;
    releaseActivePlayback = null;
  }
}

export function claimPrimedAutoplayAudioElement(): HTMLAudioElement | null {
  const element = primedElement;
  primedElement = null;

  if (element) {
    element.loop = false;
  }

  if (primedSourceUrl) {
    URL.revokeObjectURL(primedSourceUrl);
    primedSourceUrl = null;
  }

  return element;
}

export function resetAutoplayAudioElementForTests(): void {
  activePlaybackElement = null;
  releaseActivePlayback = null;
  if (primedElement) {
    primedElement.pause();
    primedElement.removeAttribute("src");
    primedElement.load();
    primedElement = null;
  }
  if (primedSourceUrl) {
    URL.revokeObjectURL(primedSourceUrl);
    primedSourceUrl = null;
  }
}
